import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * **Inert overlay** pattern: `.panel` is `position: relative`; `.shade` is `absolute` inset so it covers content.
 * `aria-busy` on the region signals async work; paired with `role="status"` on the tiny spinner for SR context.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'busy-overlay',
  category: 'loading',
  title: 'Busy overlay',
  description: 'Dimmed panel with centered spinner; aria-busy on the region.',
  tags: [T.loading, T.overlay, T.a11y],
  html: `<h2>Panel</h2>

<div class="panel" id="panel" aria-busy="false">
  <p>Click the button to fake a 2s task.</p>
  <button type="button" id="go">Start task</button>
  <!-- When shown, sits above the button in paint order — you can’t click Start again until the timer hides this (by design). -->
  <div class="shade" id="shade" hidden>
    <div class="mini" role="status" aria-label="Working"></div>
  </div>
</div>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 0.75rem;
}

.panel {
  position: relative;
  max-width: 20rem;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid var(--nogrok-preview-outline);
  background: var(--nogrok-preview-surface-container);
  min-height: 6rem;
}

.shade {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 10px;
  display: grid;
  place-items: center;
}

/* Author display:grid on .shade can beat the UA [hidden] rule in some previews — force hidden shades off. */
.shade[hidden] {
  display: none !important;
}

/* Classic CSS spinner: partial border + rotate */
.mini {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid var(--nogrok-preview-outline);
  border-top-color: var(--nogrok-preview-primary);
  animation: sp1 0.75s linear infinite;
}

button {
  font: inherit;
  padding: 0.45rem 0.85rem;
  border-radius: 6px;
  border: 1px solid var(--nogrok-preview-primary);
  background: color-mix(in srgb, var(--nogrok-preview-primary) 12.5%, transparent);
  color: var(--nogrok-preview-on-surface);
  cursor: pointer;
  margin-top: 0.5rem;
}

@keyframes sp1 {
  to {
    transform: rotate(360deg);
  }
}`,
  js: `const panel = document.getElementById('panel')
const shade = document.getElementById('shade')

document.getElementById('go')?.addEventListener('click', () => {
  shade?.removeAttribute('hidden')
  panel?.setAttribute('aria-busy', 'true')
  // Demo: pretend work finished; production would clear this in a fetch().finally() (or AbortController).
  window.setTimeout(() => {
    shade?.setAttribute('hidden', '')
    panel?.setAttribute('aria-busy', 'false')
  }, 2000)
})`,
}

export default preset
